import {
	ProctoringAssessmentConfigurationV2,
	ProctoringMethod,
	ProctoringOption,
	ProctoringOptionProfileV2,
} from "@verity/proctoring-option/types";
import { EditQuizType, ExemptedStudent, ThirdPartyPlatform, VerityInstituteSettings } from "../types/common";
import type { EnabledProctoringOptions } from "@verity/api-client/types";
import { getPlatformIdFromUrl } from "@verity/url-matcher";
import { DEFAULT_PROCTORING_OPTION, proctoringConfigurationToMethodName } from "@verity/proctoring-option";
import type { EditQuizPageProps } from "./editQuiz";

export const hasNonWhitespace = (str: string | null | undefined) => typeof str === "string" && /\S/.test(str);

export const defaultValidateUrl = (url: string | undefined | null) => {
	if (url) {
		const isValidDomain = (url: string) => {
			try {
				const normalized = url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
				const { hostname } = new URL(normalized);
				return /\.[a-zA-Z]{2,}$/.test(hostname);
			} catch {
				return false;
			}
		};
		return {
			notEmpty: url.length > 0,
			noSpaces: !url.includes(" "),
			noTrailingSlash: !url.endsWith("/") && !url.endsWith("\\"),
			hasHttp: url.startsWith("http://") || url.startsWith("https://"),
			hasDomain: isValidDomain(url),
		};
	} else {
		return {
			notEmpty: false,
			noSpaces: false,
			noTrailingSlash: false,
			hasHttp: false,
			hasDomain: false,
		};
	}
};

export function hasProctoringOptionsChanged(
	initialProctoringOption?: Partial<ProctoringOption>,
	proctoringOption?: Partial<ProctoringOption>,
) {
	if (!initialProctoringOption || !proctoringOption) return false;
	const initialProctoringOptionEntries = Object.entries(initialProctoringOption).sort();
	const proctoringOptionEntries = Object.entries(proctoringOption).sort();

	for (let i = 0; i < initialProctoringOptionEntries.length; i++) {
		const [key, value] = initialProctoringOptionEntries[i];
		const currentValue = proctoringOptionEntries[i]?.[1];

		if (key === "showRecordingPanel") {
			//showRecordingPanel is set from false (default) to true without user input when they access the 'Method' tab
			continue;
		}

		if (key === "applicationBlockList") {
			if (proctoringOption.applicationBlockMode === "off") continue;
		} else if (key === "excludedWebTrafficRecordingUrls") {
			if (!proctoringOption.recordWebTraffic) continue;
		} else if (key === "keyboardShortcutBlockList") {
			if (!proctoringOption.recordInput) continue;
		}

		if (Array.isArray(value) && Array.isArray(currentValue)) {
			const sortedInitial = [...value].sort();
			const sortedCurrent = [...currentValue].sort();
			if (JSON.stringify(sortedInitial) !== JSON.stringify(sortedCurrent)) {
				return true;
			}
		} else if (value !== currentValue) {
			return true;
		}
	}
}

type AssessmentConfig = Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">;

export function hasAssessmentConfigurationChanged(
	initialProctoringAssessmentConfiguration?: AssessmentConfig,
	proctoringAssessmentConfiguration?: AssessmentConfig,
) {
	if (!initialProctoringAssessmentConfiguration || !proctoringAssessmentConfiguration) return false;

	const initialProctoringAssessmentConfigurationEntries = Object.entries(
		initialProctoringAssessmentConfiguration,
	).sort();
	const proctoringAssessmentConfigurationRefEntries = Object.entries(proctoringAssessmentConfiguration).sort();

	for (let i = 0; i < initialProctoringAssessmentConfigurationEntries.length; i++) {
		const [key, value] = initialProctoringAssessmentConfigurationEntries[i];
		const currentValue = proctoringAssessmentConfigurationRefEntries[i]?.[1];

		if (key === "password" || key === "enabled") {
			continue;
		}

		if (key === "thirdPartyAssessmentOption") {
			if (value && currentValue) {
				const initialThirdPartyEntries = Object.entries(value).sort();
				const thirdPartyEntries = Object.entries(currentValue).sort();

				for (let i = 0; i < initialThirdPartyEntries.length; i++) {
					const [thirdPartyKey, thirdPartyValue] = initialThirdPartyEntries[i];
					const thirdPartyCurrentValue = thirdPartyEntries[i]?.[1];

					if (thirdPartyKey === "password") {
						if (
							(thirdPartyValue === null || thirdPartyValue === "") &&
							(thirdPartyCurrentValue === null || thirdPartyCurrentValue === "")
						) {
							continue;
						}
					}

					if (thirdPartyValue !== thirdPartyCurrentValue) {
						return true;
					}
				}
			}
			continue;
		}

		if (value !== currentValue) {
			return true;
		}
	}
}

/**If any proctoring options are enabled */
export function areProctoringOptionsSet(
	proctoringOption?: Partial<ProctoringOption>,
	defaultProctoringOption?: ProctoringOption | null,
) {
	// Keys for DEFAULT_PROCTORING_OPTIONs that are not toggleable restrictions (isExtension, isLockdown, etc.)
	const proctoringOptionFilteredKeys = [
		"showRecordingPanel",
		"enforceLockdown",
		"enforceMobile",
		"isExtension",
		"isLockdown",
		"isMobile",
		"isChromeOs",
		"isBrowser",
	];

	if (proctoringOption && defaultProctoringOption) {
		const proctoringOptionEntries = Object.entries(proctoringOption).sort();
		const defaultProctoringOptionEntries = Object.entries(defaultProctoringOption).sort();

		for (let i = 0; i < proctoringOptionEntries.length; i++) {
			const [key, value] = proctoringOptionEntries[i];
			const defaultValue = defaultProctoringOptionEntries[i]?.[1];

			if (proctoringOptionFilteredKeys.includes(key)) {
				continue;
			}

			if (key === "applicationBlockList") {
				if (proctoringOption.applicationBlockMode === "off") continue;
			} else if (key === "excludedWebTrafficRecordingUrls") {
				if (!proctoringOption.recordWebTraffic) continue;
			} else if (key === "keyboardShortcutBlockList") {
				if (!proctoringOption.recordInput) continue;
			}

			if (
				((Array.isArray(value) && value.length == 0) || value === null) &&
				((Array.isArray(defaultValue) && defaultValue.length == 0) || defaultValue === null)
			) {
				continue;
			}

			if (value !== defaultValue) {
				return true;
			}
		}
	}
	return false;
}

export function hasExemptedStudentMapChanged(
	initialMap?: Map<number, ExemptedStudent> | null,
	uploadedMap?: Map<number, ExemptedStudent>,
): boolean {
	if (!initialMap || !uploadedMap) return false;

	if (initialMap.size !== uploadedMap.size) {
		return true;
	}
	for (const [key, value] of initialMap) {
		const uploadedValue = uploadedMap?.get(key);
		if (!uploadedValue || JSON.stringify(uploadedValue) !== JSON.stringify(value)) {
			return true;
		}
	}
	return false;
}

export function isThirdPartyStepValid(platforms: ThirdPartyPlatform[], assessmentConfig?: AssessmentConfig) {
	if (!assessmentConfig || !assessmentConfig.thirdPartyAssessmentOption) return true;

	const { password, thirdPartyPlatformId, thirdPartyUrl } = assessmentConfig.thirdPartyAssessmentOption;

	const urlValidation = defaultValidateUrl(thirdPartyUrl);
	const allValidationsPassed = Object.values(urlValidation).every((v) => v);

	const selectedPlatform =
		typeof thirdPartyPlatformId === "number" ? platforms.find((p) => p.platformId === thirdPartyPlatformId) : undefined;

	// Check password validity
	// 1. null is valid
	// 2. string must have length after trimming
	// 3. undefined (or anything else) is invalid
	const isPasswordValid = password === null || (typeof password === "string" && password.trim().length > 0);

	return !!selectedPlatform && isPasswordValid && allValidationsPassed;
}

export function isValidPlatform<T extends Partial<ProctoringOption>>({
	option,
	verityInstituteSettings,
	quizType,
}: {
	quizType?: EditQuizType;
	option?: Pick<T, "isExtension" | "isLockdown" | "isMobile" | "isBrowser">;
	verityInstituteSettings?: VerityInstituteSettings;
}) {
	if (option?.isBrowser)
		return isBrowserPlatformOffered({ quizType, allowBrowserPlatform: verityInstituteSettings?.allowBrowserPlatform });
	return Boolean(
		option &&
			verityInstituteSettings &&
			((option.isExtension && verityInstituteSettings.allowExtensionPlatform) ||
				(option.isLockdown && verityInstituteSettings.allowLockdownPlatform) ||
				(option.isMobile && verityInstituteSettings.allowMobilePlatform)),
	);
}

export function convertProctoringAssessmentConfiguration(
	config: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">,
	forceThirdParty = false,
	initialPlatformId?: number,
) {
	const thirdPartyAssessmentOption = config.thirdPartyAssessmentOption ?? (forceThirdParty ? {} : undefined);

	// If third party, but no platform -> use platformId passed or try to get platform id from url if there is a url
	if (thirdPartyAssessmentOption && !thirdPartyAssessmentOption.thirdPartyPlatformId) {
		thirdPartyAssessmentOption.thirdPartyPlatformId =
			initialPlatformId || getPlatformIdFromUrl(thirdPartyAssessmentOption.thirdPartyUrl ?? "");
	}

	return {
		...config,
		thirdPartyAssessmentOption,
	};
}

export function convertProctoringOption(option: Partial<ProctoringOption>): Partial<ProctoringOption> {
	return {
		...option,
		hideOtherWindows: option.isLockdown && option.hideOtherWindows,
	};
}

export function getDefaultProctoringOption(verityInstituteSettings: VerityInstituteSettings, quizType?: EditQuizType) {
	const isExtension = verityInstituteSettings.allowExtensionPlatform;
	const isLockdown = !isExtension && verityInstituteSettings.allowLockdownPlatform;
	const isMobile = !isExtension && !isLockdown && verityInstituteSettings.allowMobilePlatform;
	const isBrowser =
		!isExtension &&
		!isLockdown &&
		!isMobile &&
		isBrowserPlatformOffered({ quizType, allowBrowserPlatform: verityInstituteSettings.allowBrowserPlatform });
	return { ...DEFAULT_PROCTORING_OPTION, isExtension, isLockdown, isMobile, isBrowser };
}

export function checkIfVerityAssessment(type?: EditQuizType) {
	return type === "verityAssignment" || type === "verityQuiz";
}

// An absent quizType means there is no assessment in context (institution profile creation),
// where the platform is offered on the institute setting alone.
export function isBrowserPlatformOffered({
	quizType,
	allowBrowserPlatform,
}: {
	quizType?: EditQuizType;
	allowBrowserPlatform?: boolean;
}) {
	return Boolean(allowBrowserPlatform) && (quizType === undefined || checkIfVerityAssessment(quizType));
}

export function checkIfStandaloneAssessment(type?: EditQuizType) {
	return type === "standalone";
}

export function checkIfPortalThirdPartyAssessment(type?: EditQuizType, entryPoint?: EditQuizPageProps["entryPoint"]) {
	return type === "third-party" && entryPoint === "lti-page";
}

export function checkIfLmsEntryPoint(entryPoint?: EditQuizPageProps["entryPoint"]) {
	return entryPoint === "lms-modal";
}

export function getExemptedStudentsFromMap(
	exemptedStudentsMap: Map<number, ExemptedStudent>,
	uploadedProfilesMap: Map<number, ProctoringOptionProfileV2>,
) {
	return Array.from(exemptedStudentsMap.values()).filter(
		(x) => !x.proctoringOptionProfileId || uploadedProfilesMap.has(x.proctoringOptionProfileId),
	);
}

export function isAutomatedProctoring(option: EditQuizPageProps["proctoringAssessmentConfiguration"]) {
	if (!option) return false;
	return !option.isLiveProctored && !option.isLivePopIn;
}

export function getProctoringMethod(option: EditQuizPageProps["proctoringAssessmentConfiguration"]): ProctoringMethod {
	return proctoringConfigurationToMethodName({
		isLiveProctored: Boolean(option?.isLiveProctored),
		isLivePopIn: Boolean(option?.isLivePopIn),
		enablePostReview: Boolean(option?.enablePostReview),
	});
}

export function getAvailableProctoringMethods(
	enabledProctoringOptions: EnabledProctoringOptions,
	verityInstituteSettings: VerityInstituteSettings,
): ProctoringMethod[] {
	const { liteMode, allowLiveProctoring, allowLivePopIn, allowLivePopInPostReview } = verityInstituteSettings;

	// Filtered rather than rebuilt so the institute's ordering is kept, since callers use the first entry
	return enabledProctoringOptions.proctoringMethod.filter((method) => {
		switch (method) {
			case "automated":
				return true;
			case "live":
				return !liteMode && allowLiveProctoring;
			case "pop-in":
				return !liteMode && allowLivePopIn;
			case "pop-in-post-review":
				return !liteMode && allowLivePopIn && allowLivePopInPostReview;
		}
	});
}
